import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { fetchMyProjects, type CreatorProject } from '@/infrastructure/clients/creatorApi';
import { isLiveRefresh } from '@/modules/mcpStream';
import { useProjectsChangeVersion } from './useProjectsChangeVersion';

// Four columns by five rows, less the new story card
export const PROJECTS_PER_PAGE = 19;

interface Options {
  userId: string | undefined;
  search: string;
  sort: string;
  filterKey: string;
}

export function useDashboardProjects({ userId, search, sort, filterKey }: Options) {
  const [currentPage, setCurrentPage] = useState(1);
  const [projects, setProjects] = useState<CreatorProject[]>([]);
  const [totalProjects, setTotalProjects] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  // How many projects the account had on its first unfiltered read, which is what the first-run tour asks about
  const [accountTotal, setAccountTotal] = useState<number | null>(null);
  const [reloadCount, setReloadCount] = useState(0);
  const requestSeqRef = useRef(0);
  const quietReloadRef = useRef(false);

  const [projectsUserId, setProjectsUserId] = useState(userId);
  if (projectsUserId !== userId) {
    setProjectsUserId(userId);
    setProjects([]);
    setFailed(false);
    setHasLoaded(false);
    setTotalProjects(0);
    setTotalPages(0);
    setAccountTotal(null);
  }
  useEffect(() => {
    requestSeqRef.current += 1;
  }, [userId]);

  if (!userId && loading) setLoading(false);

  const [filters, setFilters] = useState({ search, sort, filterKey });
  if (filters.search !== search || filters.sort !== sort || filters.filterKey !== filterKey) {
    setFilters({ search, sort, filterKey });
    setCurrentPage(1);
  }

  const changeVersion = useProjectsChangeVersion();
  const changeVersionSeen = useRef(changeVersion);

  useEffect(() => {
    if (!userId) return;
    const seq = ++requestSeqRef.current;
    const isCurrent = () => seq === requestSeqRef.current;
    const live = isLiveRefresh(changeVersionSeen, changeVersion) || quietReloadRef.current;
    quietReloadRef.current = false;

    const load = async () => {
      try {
        if (!live) setLoading(true);
        const page = await fetchMyProjects({ page: currentPage, pageSize: PROJECTS_PER_PAGE, search, sort });
        if (!isCurrent()) return;
        if (!page) {
          // A background read that fails keeps the cards already shown
          if (live) {
            toast.error("Couldn't refresh your projects.", { id: 'projects-refresh-failed' });
            return;
          }
          setProjects([]);
          setTotalPages(0);
          setFailed(true);
          return;
        }
        const pages = Math.max(1, Math.ceil(page.totalProjects / page.pageSize));
        if (page.projects.length === 0 && currentPage > pages) {
          quietReloadRef.current = live;
          setCurrentPage(pages);
          return;
        }
        setFailed(false);
        setProjects(page.projects);
        setTotalProjects(page.totalProjects);
        setTotalPages(pages);
        if (!search) setAccountTotal(prev => prev ?? page.totalProjects);
      } catch (error) {
        console.error('Error fetching projects:', error);
        if (isCurrent() && !live) setFailed(true);
      } finally {
        if (isCurrent()) {
          setLoading(false);
          setHasLoaded(true);
        }
      }
    };

    void load();
  }, [userId, currentPage, search, sort, changeVersion, reloadCount]);

  const retry = () => setReloadCount(n => n + 1);

  // Takes a deleted project off the page at once, then reads the page again in place
  const removeProject = (projectId: string) => {
    setProjects(prev => prev.filter(p => p.project.uniqueId !== projectId));
    setTotalProjects(prev => Math.max(0, prev - 1));
    quietReloadRef.current = true;
    setReloadCount(n => n + 1);
  };

  return {
    projects,
    totalProjects,
    accountTotal,
    totalPages,
    currentPage,
    setCurrentPage,
    loading,
    hasLoaded,
    failed,
    retry,
    removeProject,
  };
}
